import { DeploymentPhase } from "@/interfaces";
import { useDeploymentStatus } from "@/services/deploymentService";
import { useUserStore } from "@/stores/userStore";
import { Alert, Button, Modal } from "antd";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";

const ACK_STORAGE_PREFIX = "deployment-notice-ack:";
// The "save your work" warning can't be dismissed before this many seconds
const WARNING_MIN_VISIBLE_SECONDS = 10;

const readAck = (deploymentId: string): boolean => {
  try {
    return localStorage.getItem(`${ACK_STORAGE_PREFIX}${deploymentId}`) === "1";
  } catch {
    return false;
  }
};

const writeAck = (deploymentId: string) => {
  try {
    localStorage.setItem(`${ACK_STORAGE_PREFIX}${deploymentId}`, "1");
  } catch {
    // Storage unavailable: the warning just shows again after a reload
  }
};

// "4:05", or "2:04:05" when users are notified hours ahead
const formatCountdown = (ms: number) => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const mmss = `${minutes.toString().padStart(hours ? 2 : 1, "0")}:${seconds.toString().padStart(2, "0")}`;
  return hours ? `${hours}:${mmss}` : mmss;
};

/**
 * App-wide deployment notices, driven by GET /deployment/status:
 * - logged-in users get a "save your work" warning (dismissable only after 10s), then a countdown banner
 * - while logins are blocked (logged-out users) or the deployment runs (everyone), a blocking popup
 * The page reloads when a deployment it saw running finishes, so users pick up the new build.
 */
const DeploymentNotice = () => {
  const isAuthenticated = useUserStore((state) => state.isAuthenticated);
  const { data, dataUpdatedAt } = useDeploymentStatus();
  const deployment = data?.deployment ?? null;

  const [now, setNow] = useState(() => Date.now());
  const [ackedId, setAckedId] = useState<string | null>(null);
  const [warningShownAt, setWarningShownAt] = useState<number | null>(null);
  const sawInProgress = useRef(false);

  // Tick every second while a deployment is active to drive the countdowns
  useEffect(() => {
    if (!deployment) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [deployment]);

  // Correct for the browser's clock: deployment times come from the server's clock
  const clockOffset = data ? Date.parse(data.serverTime) - dataUpdatedAt : 0;
  const serverNow = now + clockOffset;

  // Advance the phase locally between polls so the popups switch exactly on time
  let phase: DeploymentPhase | null = deployment?.phase ?? null;
  if (deployment && phase !== "inProgress") {
    if (serverNow >= Date.parse(deployment.deployAt)) phase = "inProgress";
    else if (serverNow >= Date.parse(deployment.loginBlockAt)) phase = "loginBlocked";
  }

  const isAcked = !!deployment && (ackedId === deployment.id || readAck(deployment.id));
  const showWarning = isAuthenticated && !!deployment && phase !== "inProgress" && !isAcked;
  const showBanner = isAuthenticated && !!deployment && phase !== "inProgress" && isAcked;
  const showBlocking = phase === "inProgress" || (phase === "loginBlocked" && !isAuthenticated);

  useEffect(() => {
    if (showWarning) setWarningShownAt((shownAt) => shownAt ?? Date.now());
    else setWarningShownAt(null);
  }, [showWarning]);

  useEffect(() => {
    if (phase === "inProgress") {
      sawInProgress.current = true;
    } else if (data && !data.deployment && sawInProgress.current) {
      window.location.reload();
    }
  }, [phase, data]);

  if (!deployment) return null;

  const deployAtLabel = dayjs(deployment.deployAt).format("hh:mm A");
  const timeToDeploy = formatCountdown(Date.parse(deployment.deployAt) - serverNow);
  const warningSecondsLeft = warningShownAt
    ? Math.max(0, WARNING_MIN_VISIBLE_SECONDS - Math.floor((now - warningShownAt) / 1000))
    : WARNING_MIN_VISIBLE_SECONDS;

  const handleAcknowledge = () => {
    writeAck(deployment.id);
    setAckedId(deployment.id);
  };

  return (
    <>
      <Modal
        open={showWarning}
        title="Scheduled deployment"
        closable={false}
        maskClosable={false}
        keyboard={false}
        centered
        footer={
          <Button type="primary" disabled={warningSecondsLeft > 0} onClick={handleAcknowledge}>
            {warningSecondsLeft > 0 ? `I understand (${warningSecondsLeft})` : "I understand"}
          </Button>
        }
      >
        <div className="flex flex-col gap-2">
          <p>
            A deployment will start at <strong>{deployAtLabel}</strong> (in {timeToDeploy}).
          </p>
          <p>
            Please save your work. The application will be unavailable for a few minutes while the deployment
            runs, and new logins will be disabled shortly before it starts.
          </p>
          {deployment.message && <p className="text-gray-600">{deployment.message}</p>}
        </div>
      </Modal>

      <Modal
        open={showBlocking}
        title="Deployment in progress"
        closable={false}
        maskClosable={false}
        keyboard={false}
        centered
        footer={null}
      >
        <div className="flex flex-col gap-2">
          {phase === "inProgress" ? (
            <p>
              We are deploying an update. The application will be back in a few minutes and this page will refresh
              automatically.
            </p>
          ) : (
            <p>
              A deployment starts at <strong>{deployAtLabel}</strong>, so login is temporarily disabled. Please try
              again in a few minutes.
            </p>
          )}
          {deployment.message && <p className="text-gray-600">{deployment.message}</p>}
        </div>
      </Modal>

      {showBanner && (
        <div className="fixed bottom-4 left-1/2 z-[1001] w-[calc(100%-2rem)] max-w-xl -translate-x-1/2">
          <Alert
            type="warning"
            showIcon
            message={`Deployment starts at ${deployAtLabel} (in ${timeToDeploy}). Please save your work.`}
          />
        </div>
      )}
    </>
  );
};

export default DeploymentNotice;
